import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  createCustomerPortalSession,
  getShopifySubscription,
  removeUpcomingSubscriptionPause,
} from '../../api/membershipAPI';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  getCurrentProfileAsync,
  getProfileDetailAsync,
  getProfileMembershipAsync,
} from '../../store/profileSlice';
import {DateTime} from 'luxon';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {CurrentMembershipLevels, MembershipLevels, Roles} from '../../enums';
import {Helmet} from 'react-helmet';
import {useParams} from 'react-router-dom';
import {getChildProfile} from '../../services/familyService';
import PageTitle from '../shared/PageTitle';
import {useTranslation} from 'react-i18next';
import ProfileIdentity from '../shared/ProfileIdentity';
import FamilyMemberList from '../shared/FamilyMemberList';
import MembershipTier from './MembershipTier';
import {getDevicesAsync} from '../../store/deviceSlice';
import PauseMembershipModal from './PauseMembershipModal';
import AccessShopifySubscriptionModal from './AccessShopifySubscriptionModal';
import ShopifyMembershipClaimModal from './ShopifyMembershipClaimModal';
import {ShopifySubscription} from '../../interfaces';
import ResumeMembershipModal from './ResumeMembershipModal';
import CancelMembershipModal from './CancelMembershipModal';
import StripeMembershipChangeModal from './StripeMembershipChangeModal';
import MembershipTierV2 from './MembershipTierV2';
import ModifyShopifySubscriptionModal from './ModifyShopifySubscriptionModal';
import {getMembershipLevelName} from '../../services/membershipService';

interface Props {}

const Membership: React.FC<Props> = () => {
  const {t} = useTranslation();

  const {profileId} = useParams();
  const profileIdInt = parseInt(profileId || '');

  const dispatch = useAppDispatch();

  const device = useAppSelector(state => state.device.devices?.[0]);
  const role = useAppSelector(state => state.profile.profile?.role);
  const stripeCustomerId = useAppSelector(
    state => state.profile?.profile?.stripeCustomerId,
  );
  const profileDetail = useAppSelector(state => state.profile?.profileDetail);
  const profileCurrent = useAppSelector(state => state.profile?.profile);
  const membershipCurrent = useAppSelector(
    state => state.profile?.membership?.current,
  );
  const membershipNext = useAppSelector(
    state => state.profile?.membership?.next,
  );
  const profileStatus = useAppSelector(state => state.profile?.status);
  const family = useAppSelector(state => state.family?.families?.[0]);
  const childProfiles = useAppSelector(
    state => state.family?.families?.[0]?.childProfiles,
  );

  const [showShopifySubscriptionModal, setShowShopifySubscriptionModal] =
    useState<boolean>(false);
  const [showMembershipLevelModal, setShowMembershipLevelModal] =
    useState<boolean>(false);
  const [membershipLevel, setMembershipLevel] = useState<string>('');
  const [updateMembershipLevelError, setUpdateMembershipLevelError] =
    useState<string>('');
  const [showPauseMembershipModal, setShowPauseMembershipModal] =
    useState<boolean>(false);
  const [showResumeMembershipModal, setShowResumeMembershipModal] =
    useState<boolean>(false);
  const [
    showAccessShopifySubscriptionModal,
    setShowAccessShopifySubscriptionModal,
  ] = useState<boolean>(false);
  const [
    showCancelShopifySubscriptionModal,
    setShowCancelShopifySubscriptionModal,
  ] = useState<boolean>(false);
  const [
    showModifyShopifySubscriptionModal,
    setShowModifyShopifySubscriptionModal,
  ] = useState<boolean>(false);
  const [shopifySubscriptionData, setShopifySubscriptionData] =
    useState<ShopifySubscription | null>(null);
  const [shopifySubscriptionDataLoading, setShopifySubscriptionDataLoading] =
    useState<boolean>(false);
  const [pendingMembershipChange, setPendingMembershipChange] =
    useState<boolean>(false);

  const profile = useMemo(
    () => (profileIdInt ? profileDetail : profileCurrent),
    [profileIdInt, profileDetail, profileCurrent],
  );

  useEffect(() => {
    // get the child profile if necessary
    if (profileIdInt) {
      dispatch(getProfileDetailAsync({profileId: profileIdInt}));
    }
    dispatch(getDevicesAsync({profileId: profileIdInt || undefined}));
    if (profileIdInt || stripeCustomerId) {
      dispatch(
        getProfileMembershipAsync({profileId: profileIdInt || undefined}),
      );
    }
  }, [stripeCustomerId, dispatch, profileIdInt]);

  const title = t('navigation.pageTitle', {pageName: t('pages.membership')});

  const childProfile = useMemo(
    () =>
      getChildProfile({
        family,
        profileId: profileIdInt,
      }),
    [family, profileIdInt],
  );

  const fetchShopifySubscription = useCallback(async () => {
    setShopifySubscriptionDataLoading(true);
    const res = await getShopifySubscription({
      id: profile?.shopifySubscription?.id,
    });
    setShopifySubscriptionDataLoading(false);
    setShopifySubscriptionData(res.data.data.shopifySubscription);
  }, [profile?.shopifySubscription?.id]);

  // fetch the shopifySubscription whenever the profile changes
  useEffect(() => {
    if (profile?.shopifySubscription?.id) {
      fetchShopifySubscription();
    }
  }, [fetchShopifySubscription, profile?.shopifySubscription?.id]);

  const activeShopifySubscription = useMemo(
    () => profile?.shopifySubscription,
    [profile?.shopifySubscription],
  );
  const activeStripeSubscription = useMemo(
    () =>
      membershipCurrent &&
      membershipCurrent.name !== MembershipLevels.rec &&
      !membershipCurrent.isTrial,
    [membershipCurrent],
  );
  const showStripeSubscriptionManagement = useMemo(
    () =>
      !activeShopifySubscription &&
      (device?.is12MonthMembership ||
        device?.hasStartedShopifySubscription ||
        activeStripeSubscription),
    [
      activeShopifySubscription,
      activeStripeSubscription,
      device?.hasStartedShopifySubscription,
      device?.is12MonthMembership,
    ],
  );

  const deviceRequiresShopifySubscription = useMemo(() => {
    return (
      device &&
      !device?.is12MonthMembership &&
      !device?.hasStartedShopifySubscription
    );
  }, [device]);

  const activeTrial = useMemo(() => {
    return (
      !activeShopifySubscription &&
      DateTime.fromISO(profile?.membershipTrialEndDate) > DateTime.now()
    );
  }, [activeShopifySubscription, profile?.membershipTrialEndDate]);

  const activeStripeMembershipCurrent = useMemo(() => {
    return (
      !activeShopifySubscription &&
      membershipCurrent &&
      !deviceRequiresShopifySubscription
    );
  }, [
    activeShopifySubscription,
    membershipCurrent,
    deviceRequiresShopifySubscription,
  ]);

  const activeStripeMembershipNext = useMemo(() => {
    return (
      !activeShopifySubscription &&
      membershipNext &&
      !deviceRequiresShopifySubscription
    );
  }, [
    activeShopifySubscription,
    membershipNext,
    deviceRequiresShopifySubscription,
  ]);

  const currency = useMemo(() => {
    if (shopifySubscriptionData?.currency) {
      return shopifySubscriptionData?.currency;
    } else if (membershipCurrent?.currency) {
      if (membershipCurrent?.currency.toUpperCase() === 'CAD') {
        return 'CAD';
      } else {
        return 'USD';
      }
    } else if (profile?.country === 'Canada') {
      return 'CAD';
    } else {
      return 'USD';
    }
  }, [
    shopifySubscriptionData?.currency,
    membershipCurrent?.currency,
    profile?.country,
  ]);

  const handleRemoveUpcomingPause = useCallback(async () => {
    if (!shopifySubscriptionData?.id) {
      window.alert(t('membership.unknownSubscription'));
      return;
    }
    setPendingMembershipChange(true);
    await removeUpcomingSubscriptionPause({
      shopifySubscriptionId: shopifySubscriptionData?.id,
    });
    await fetchShopifySubscription();
    setPendingMembershipChange(false);
  }, [fetchShopifySubscription, shopifySubscriptionData?.id, t]);

  const handleShowPauseMembershipModal = useCallback(() => {
    setShowPauseMembershipModal(true);
  }, []);

  const handleShowAccessShopifySubscriptionModal = useCallback(() => {
    setShowAccessShopifySubscriptionModal(true);
  }, []);

  const handleShowResumeMembershipModal = useCallback(() => {
    setShowResumeMembershipModal(true);
  }, []);

  const handleShowCancelMembershipModal = useCallback(() => {
    setShowCancelShopifySubscriptionModal(true);
  }, []);

  const handleSelect1Month = useCallback(() => {
    if (activeShopifySubscription) {
      setShowModifyShopifySubscriptionModal(true);
      return;
    }
    setMembershipLevel(MembershipLevels.proMonth);
    setShowMembershipLevelModal(true);
  }, [activeShopifySubscription]);

  const handleSelect12Month = useCallback(() => {
    if (activeShopifySubscription) {
      setShowModifyShopifySubscriptionModal(true);
      return;
    }
    setMembershipLevel(MembershipLevels.proYear);
    setShowMembershipLevelModal(true);
  }, [activeShopifySubscription]);

  const handleSelect24Month = useCallback(() => {
    if (activeShopifySubscription) {
      setShowModifyShopifySubscriptionModal(true);
      return;
    }
    setMembershipLevel(MembershipLevels.pro24Months);
    setShowMembershipLevelModal(true);
  }, [activeShopifySubscription]);

  if (profileStatus === 'loading' || !profile) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <PageTitle>{t('pages.membership')}</PageTitle>
        {childProfile && (
          <ProfileIdentity
            sx={{mb: 2}}
            firstName={childProfile?.firstName}
            lastName={childProfile?.lastName}
            position={childProfile?.position}
            profilePictureUrl={childProfile?.profilePictureUrl}
          />
        )}

        {/* Only show Paper if it has content */}
        {(shopifySubscriptionDataLoading ||
          shopifySubscriptionData ||
          activeTrial ||
          activeStripeMembershipCurrent ||
          activeStripeMembershipNext ||
          showStripeSubscriptionManagement) && (
          <Paper sx={{p: 2, mt: 2, mb: 2}}>
            {shopifySubscriptionDataLoading && (
              <Box sx={{display: 'flex', justifyContent: 'center'}}>
                <CircularProgress />
              </Box>
            )}
            {shopifySubscriptionData && (
              <>
                {shopifySubscriptionData?.orderIntervalFrequency &&
                  shopifySubscriptionData?.rechargeSubscriptionData
                    ?.intervalUnit && (
                    <Typography variant="h5" sx={{mb: 2}}>
                      {t('membership.billingFrequency', {
                        intervalFrequency:
                          shopifySubscriptionData?.orderIntervalFrequency,
                        // capitalize the first letter
                        intervalUnit:
                          (shopifySubscriptionData?.orderIntervalUnit ?? '')
                            .length > 0
                            ? (
                                shopifySubscriptionData?.orderIntervalUnit as string
                              )
                                .charAt(0)
                                .toUpperCase() +
                              shopifySubscriptionData?.orderIntervalUnit?.slice(
                                1,
                              )
                            : shopifySubscriptionData?.orderIntervalUnit,
                      })}
                    </Typography>
                  )}
                {shopifySubscriptionData?.pauseStartDate &&
                  shopifySubscriptionData?.pauseEndDate && (
                    <Typography variant="h5" sx={{mb: 2}}>
                      {t('membership.pauseRange', {
                        startDate: DateTime.fromISO(
                          shopifySubscriptionData?.pauseStartDate,
                        ).toFormat('M/d/yyyy'),
                        endDate: DateTime.fromISO(
                          shopifySubscriptionData?.pauseEndDate,
                        ).toFormat('M/d/yyyy'),
                      })}
                    </Typography>
                  )}
                {shopifySubscriptionData?.rechargeSubscriptionData
                  ?.nextChargeScheduledAt && (
                  <Typography variant="h5" sx={{mb: 2}}>
                    {t('membership.nextChargeScheduledAt', {
                      date: DateTime.fromFormat(
                        shopifySubscriptionData?.rechargeSubscriptionData
                          ?.nextChargeScheduledAt,
                        'yyyy-MM-dd',
                      ).toFormat('M/d/yyyy'),
                    })}
                  </Typography>
                )}
                {shopifySubscriptionData?.rechargeSubscriptionData &&
                  !shopifySubscriptionData?.rechargeSubscriptionData
                    ?.nextChargeScheduledAt &&
                  !shopifySubscriptionData?.expiringAt && (
                    <Typography variant="h5" sx={{mb: 2}}>
                      {t('membership.membershipExpiring')}
                    </Typography>
                  )}
                {shopifySubscriptionData?.rechargeSubscriptionData &&
                  !shopifySubscriptionData?.rechargeSubscriptionData
                    ?.nextChargeScheduledAt &&
                  shopifySubscriptionData?.expiringAt && (
                    <Typography variant="h5" sx={{mb: 2}}>
                      {t('membership.membershipExpiresOn', {
                        date: DateTime.fromISO(
                          shopifySubscriptionData?.expiringAt ?? '',
                        ).toFormat('M/d/yyyy'),
                      })}
                    </Typography>
                  )}
                {shopifySubscriptionData?.returnOrderName &&
                  shopifySubscriptionData?.returnStatus === 'pending' && (
                    <Alert severity="warning" variant="outlined" sx={{mb: 2}}>
                      <Box sx={{mb: 2}}>
                        {(
                          t(
                            'membership.cancelMembershipReturnInstructionsStart',
                            {
                              returnObjects: true,
                            },
                          ) as string[]
                        ).map(instruction => (
                          <Typography key={instruction} variant="body1">
                            {instruction}
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{mb: 2}}>
                        {(
                          t(
                            'membership.cancelMembershipReturnInstructionsAddress',
                            {
                              returnObjects: true,
                              returnOrderName:
                                shopifySubscriptionData?.returnOrderName,
                            },
                          ) as string[]
                        ).map(instruction => (
                          <Typography key={instruction} variant="body1">
                            {instruction}
                          </Typography>
                        ))}
                      </Box>
                      <Box sx={{mb: 2}}>
                        {(
                          t(
                            'membership.cancelMembershipReturnInstructionsEnd',
                            {
                              returnObjects: true,
                            },
                          ) as string[]
                        ).map(instruction => (
                          <Typography key={instruction} variant="body1">
                            {instruction}
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}
                {/* shopify subscription management */}
                {activeShopifySubscription &&
                  // only show buttons if there is a next charge scheduled
                  shopifySubscriptionData?.rechargeSubscriptionData
                    ?.nextChargeScheduledAt && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: {xs: 'center', md: 'flex-end'},
                        flexWrap: 'wrap',
                      }}>
                      {!shopifySubscriptionData?.pauseStartDate &&
                        shopifySubscriptionData?.plan === '1_month' && (
                          <Button
                            variant="outlined"
                            sx={{mt: 1, mr: 1}}
                            disabled={pendingMembershipChange}
                            onClick={handleShowPauseMembershipModal}>
                            {t('membership.pauseMembership')}
                          </Button>
                        )}
                      {shopifySubscriptionData?.pauseStartDate &&
                        DateTime.fromISO(
                          shopifySubscriptionData?.pauseStartDate,
                        ) > DateTime.now() && (
                          <Button
                            variant="contained"
                            sx={{mt: 1, mr: 1}}
                            disabled={pendingMembershipChange}
                            onClick={handleRemoveUpcomingPause}>
                            {t('membership.removeUpcomingPause')}
                          </Button>
                        )}
                      {shopifySubscriptionData?.pauseStartDate &&
                        DateTime.fromISO(
                          shopifySubscriptionData?.pauseStartDate,
                        ) < DateTime.now() && (
                          <Button
                            variant="contained"
                            sx={{mt: 1, mr: 1}}
                            disabled={pendingMembershipChange}
                            onClick={handleShowResumeMembershipModal}>
                            {t('membership.resumeMembership')}
                          </Button>
                        )}
                      <Button
                        variant="outlined"
                        sx={{mt: 1, mr: 1}}
                        disabled={pendingMembershipChange}
                        onClick={handleShowAccessShopifySubscriptionModal}>
                        {t('membership.updatePaymentInformation')}
                      </Button>
                      <Button
                        variant="text"
                        sx={{mt: 1, mr: 1, fontWeight: 300}}
                        disabled={pendingMembershipChange}
                        onClick={handleShowCancelMembershipModal}>
                        {t('membership.cancelMembership')}
                      </Button>
                    </Box>
                  )}
              </>
            )}
            {/* stripe info */}
            {/* user has a trial period in the future */}
            {activeTrial ? (
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                }}>
                {t('membership.yourIntroMembershipWillEndOn', {
                  date: DateTime.fromISO(
                    profile?.membershipTrialEndDate,
                  ).toFormat('M/d/yyyy'),
                })}
              </Typography>
            ) : null}
            {activeStripeMembershipCurrent && (
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                }}>
                {t('membership.yourCurrentPlanIs', {
                  plan: `${getMembershipLevelName(membershipCurrent.name)}${
                    membershipCurrent.isTrial ? ` (${t('general.trial')})` : ''
                  }`,
                })}
              </Typography>
            )}
            {activeStripeMembershipCurrent &&
              membershipCurrent?.isTrial &&
              !activeStripeMembershipNext && (
                <Typography variant="h5" sx={{mb: 2}}>
                  {t('membership.onDateYouWillChangeTo', {
                    date: DateTime.fromISO(
                      profile?.membershipTrialEndDate,
                    ).toFormat('M/d/yyyy'),
                    plan: `${getMembershipLevelName(membershipCurrent.name)}`,
                  })}
                </Typography>
              )}
            {activeStripeMembershipNext && (
              <Typography variant="h5" sx={{mb: 2}}>
                {t('membership.onDateYouWillChangeTo', {
                  date: DateTime.fromISO(membershipNext.startDate).toFormat(
                    'M/d/yyyy',
                  ),
                  plan: `${getMembershipLevelName(membershipNext.name)}${
                    membershipNext.isTrial ? ` (${t('general.trial')})` : ''
                  }`,
                })}
              </Typography>
            )}
            {/* stripe subscription management */}
            {showStripeSubscriptionManagement && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                }}>
                <Button
                  variant="contained"
                  sx={{mb: 1, mr: 1}}
                  onClick={async () => {
                    const res = await createCustomerPortalSession({
                      profileId: profile.id,
                    });
                    if (res?.url) {
                      window.location.href = res.url;
                    } else {
                      window.alert(t('general.somethingWentWrong'));
                    }
                  }}>
                  {t('membership.updatePaymentInformation')}
                </Button>
                <Button
                  variant="text"
                  sx={{fontWeight: 300, mb: 1}}
                  onClick={() => {
                    setMembershipLevel(MembershipLevels.rec);
                    setShowMembershipLevelModal(true);
                  }}>
                  {t('membership.cancelMembership')}
                </Button>
              </Box>
            )}
          </Paper>
        )}
        {role === Roles.parent && !profileId ? (
          <Box sx={{mb: 3}}>
            <FamilyMemberList
              familyMembers={[...(childProfiles ?? [])]}
              showMembershipLinkReload={true}
            />
          </Box>
        ) : (
          <>
            {/* if there is no active subscription and no paired device show an error */}
            {!device &&
              !activeShopifySubscription &&
              !activeStripeSubscription && (
                <Alert severity="warning" variant="outlined" sx={{mb: 2}}>
                  {t('membership.pairCoreInstructions')}
                </Alert>
              )}
            {/* show elite plan deprecation warning */}
            {profile?.membershipLevel === CurrentMembershipLevels.elite && (
              <Alert severity="warning" variant="outlined" sx={{mb: 2}}>
                {t('membership.elitePlanDeprecationWarning')}
              </Alert>
            )}
            {/* if there is a device set for shopify and no active subscription */}
            {device &&
              deviceRequiresShopifySubscription &&
              !activeShopifySubscription && (
                <>
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      mb: 2,
                      '& .MuiAlert-message': {
                        width: '100%',
                      },
                    }}>
                    <Box sx={{mb: 2}}>
                      {t('membership.shopifySubscriptionInstructions')}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                      }}>
                      <Button
                        variant="contained"
                        sx={{alignSelf: 'flex-end'}}
                        onClick={async () => {
                          setShowShopifySubscriptionModal(true);
                        }}>
                        {t('membership.claimMembership')}
                      </Button>
                    </Box>
                  </Alert>
                  <Typography variant="body1" sx={{my: 5, textAlign: 'center'}}>
                    {t('membership.claimMembershipNote')}
                  </Typography>
                </>
              )}
            {/* membership tier columns with buttons for managing stripe subscriptions */}
            {(device ||
              activeShopifySubscription ||
              activeStripeSubscription) &&
              !(
                device &&
                deviceRequiresShopifySubscription &&
                !activeShopifySubscription
              ) && (
                <Grid
                  container
                  spacing={3}
                  sx={{
                    mb: 3,
                    justifyContent: 'center',
                    alignItems: 'flex-end',
                  }}>
                  {/* if the user is elite, show the old MembershipTiers */}
                  {profile?.membershipLevel ===
                    CurrentMembershipLevels.elite && (
                    <>
                      <Grid item xs={12} md={6}>
                        <MembershipTier
                          isActive={
                            profile?.membershipLevel ===
                            CurrentMembershipLevels.elite
                          }
                          membershipLevel={CurrentMembershipLevels.elite}
                          showButtons={showStripeSubscriptionManagement}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MembershipTier
                          isActive={
                            profile?.membershipLevel ===
                            CurrentMembershipLevels.pro
                          }
                          membershipLevel={CurrentMembershipLevels.pro}
                          showButtons={showStripeSubscriptionManagement}
                          membershipButtons={[
                            {
                              membershipLevel: MembershipLevels.proMonth,
                              onClick: () => {
                                setMembershipLevel(MembershipLevels.proMonth);
                                setShowMembershipLevelModal(true);
                              },
                            },
                            {
                              membershipLevel: MembershipLevels.proYear,
                              onClick: () => {
                                setMembershipLevel(MembershipLevels.proYear);
                                setShowMembershipLevelModal(true);
                              },
                            },
                            {
                              membershipLevel: MembershipLevels.pro24Months,
                              onClick: () => {
                                setMembershipLevel(
                                  MembershipLevels.pro24Months,
                                );
                                setShowMembershipLevelModal(true);
                              },
                            },
                          ]}
                        />
                      </Grid>
                    </>
                  )}
                  {profile?.membershipLevel !==
                    CurrentMembershipLevels.elite && (
                    <>
                      <Grid item xs={12} md={4}>
                        <MembershipTierV2
                          plan="24_month"
                          currency={currency}
                          active={
                            shopifySubscriptionData?.plan === '24_month' ||
                            (membershipCurrent?.name ===
                              MembershipLevels.pro24Months &&
                              !membershipCurrent?.isTrial)
                          }
                          blur={
                            shopifySubscriptionData?.plan !== '24_month' &&
                            (membershipCurrent?.name !==
                              MembershipLevels.pro24Months ||
                              membershipCurrent?.isTrial)
                          }
                          onClick={handleSelect24Month}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MembershipTierV2
                          plan="12_month"
                          currency={currency}
                          active={
                            shopifySubscriptionData?.plan === '12_month' ||
                            (membershipCurrent?.name ===
                              MembershipLevels.proYear &&
                              !membershipCurrent?.isTrial)
                          }
                          blur={
                            shopifySubscriptionData?.plan !== '12_month' &&
                            (membershipCurrent?.name !==
                              MembershipLevels.proYear ||
                              membershipCurrent?.isTrial)
                          }
                          onClick={handleSelect12Month}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <MembershipTierV2
                          plan="1_month"
                          currency={currency}
                          active={
                            shopifySubscriptionData?.plan === '1_month' ||
                            (membershipCurrent?.name ===
                              MembershipLevels.proMonth &&
                              !membershipCurrent?.isTrial)
                          }
                          blur={
                            shopifySubscriptionData?.plan !== '1_month' &&
                            (membershipCurrent?.name !==
                              MembershipLevels.proMonth ||
                              membershipCurrent?.isTrial)
                          }
                          onClick={handleSelect1Month}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Alert severity="info" variant="outlined" sx={{mb: 2}}>
                          {t('membership.membershipCurrencyNote')}
                        </Alert>
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
          </>
        )}
      </Container>
      <StripeMembershipChangeModal
        open={showMembershipLevelModal}
        setOpen={setShowMembershipLevelModal}
        membershipLevel={membershipLevel}
        updateMembershipLevelError={updateMembershipLevelError}
        setUpdateMembershipLevelError={setUpdateMembershipLevelError}
        profile={childProfile ?? profile}
        profileIdInt={profileIdInt}
      />
      <ShopifyMembershipClaimModal
        open={showShopifySubscriptionModal}
        setOpen={setShowShopifySubscriptionModal}
        profileId={profile?.id}
        onSuccess={() => {
          if (profileIdInt) {
            dispatch(getProfileDetailAsync({profileId: profileIdInt}));
          } else {
            dispatch(getCurrentProfileAsync());
          }
        }}
      />
      <PauseMembershipModal
        open={showPauseMembershipModal}
        setOpen={setShowPauseMembershipModal}
        quantity={shopifySubscriptionData?.rechargeSubscriptionData?.quantity}
        shopifySubscriptionId={shopifySubscriptionData?.id}
        pauseStartDate={DateTime.fromFormat(
          shopifySubscriptionData?.rechargeSubscriptionData
            ?.nextChargeScheduledAt ?? '',
          'yyyy-MM-dd',
        )}
        onStart={() => {
          setPendingMembershipChange(true);
        }}
        onSuccess={async () => {
          await fetchShopifySubscription();
          setPendingMembershipChange(false);
        }}
        onError={() => {
          setPendingMembershipChange(false);
        }}
      />
      <ResumeMembershipModal
        open={showResumeMembershipModal}
        setOpen={setShowResumeMembershipModal}
        quantity={shopifySubscriptionData?.rechargeSubscriptionData?.quantity}
        shopifySubscriptionId={shopifySubscriptionData?.id}
        onStart={() => {
          setPendingMembershipChange(true);
        }}
        onSuccess={async () => {
          await fetchShopifySubscription();
          setPendingMembershipChange(false);
        }}
      />
      <AccessShopifySubscriptionModal
        open={showAccessShopifySubscriptionModal}
        setOpen={setShowAccessShopifySubscriptionModal}
        quantity={shopifySubscriptionData?.rechargeSubscriptionData?.quantity}
        email={shopifySubscriptionData?.email}
      />
      <ModifyShopifySubscriptionModal
        open={showModifyShopifySubscriptionModal}
        setOpen={setShowModifyShopifySubscriptionModal}
      />
      <CancelMembershipModal
        open={showCancelShopifySubscriptionModal}
        setOpen={setShowCancelShopifySubscriptionModal}
        quantity={shopifySubscriptionData?.rechargeSubscriptionData?.quantity}
        isMonthly={shopifySubscriptionData?.isMonthly}
        shopifySubscriptionId={shopifySubscriptionData?.id}
        createdAt={shopifySubscriptionData?.createdAt}
        onStart={() => {
          setPendingMembershipChange(true);
        }}
        onSuccess={async () => {
          await fetchShopifySubscription();
          setPendingMembershipChange(false);
        }}
        onError={() => {
          setPendingMembershipChange(false);
        }}
      />
    </>
  );
};

export default Membership;
